/*
 * Connect app and Edit app forms: pure model, no React and nothing that touches `window`. Per-field validation, how the
 * name feeds the slug, what the Bitrise link shows, and the request bodies.
 */
import type { App, AppFramework } from '@hangar/shared';
import type { AppCreateInput, AppPatchInput } from '@/api/endpoints';
import { applyName, applySlug, shownError } from '@/lib/formModel';
import { SLUG_HINT, SLUG_RE } from '@/lib/format';

export const EAS_ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/* ---------- Field rules (the same ones in Connect and Edit) ---------- */

export const nameProblem = (name: string) => (!name.trim() ? 'Name is required' : name.length > 120 ? 'Keep the name under 120 characters' : undefined);

export const slugProblem = (slug: string) => (!slug ? 'Slug is required' : !SLUG_RE.test(slug) ? SLUG_HINT : undefined);

export const workingDirectoryProblem = (dir: string) => (!dir.trim() ? 'Working directory is required (use . for the repository root)' : dir.length > 200 ? 'Too long' : undefined);

export const configPathProblem = (path: string) => (!path.trim() ? 'Config path is required' : path.length > 200 ? 'Too long' : undefined);

export const easProjectIdProblem = (id: string) => (id.trim() && !EAS_ID_RE.test(id.trim()) ? 'An EAS project id is a UUID (expo.dev → project settings)' : undefined);

/* ---------- Connect app ---------- */

export interface ConnectAppValues {
  name: string;
  slug: string;
  /** Once typed by hand the slug stops following the name. */
  slugTouched: boolean;
  framework: AppFramework | '';
  workingDirectory: string;
  configPath: string;
  easProjectId: string;
  bitriseAppSlug: string;
}

/** The fields the form validates, in the order the first invalid one gets the focus. */
export type ConnectField = 'slug' | 'name' | 'framework' | 'workingDirectory' | 'configPath' | 'easProjectId';

/** Without native builds only Expo can be picked, so it starts selected. */
export const initialConnectValues = (nativeBuilds: boolean): ConnectAppValues => ({
  name: '',
  slug: '',
  slugTouched: false,
  framework: nativeBuilds ? '' : 'expo',
  workingDirectory: '.',
  configPath: 'hangar.yml',
  easProjectId: '',
  bitriseAppSlug: '',
});

/** Android and iOS native apps: the generated native workflows build them (the API's `BITRISE_NATIVE_FRAMEWORKS`); Expo and React Native use the build service. */
export const isNativeFramework = (framework: AppFramework | ''): boolean => framework === 'android_native' || framework === 'ios_native';

/** Native apps build on Bitrise (once the API does): they link a Bitrise app instead of an EAS project. */
export const isNativeOnBitrise = (nativeBuilds: boolean, framework: AppFramework | '') => nativeBuilds && isNativeFramework(framework);

/** One field changed to `raw` (the select's value for the framework). */
export function applyConnectChange(v: ConnectAppValues, field: ConnectField, raw: string): ConnectAppValues {
  if (field === 'name') return applyName(v, raw);
  if (field === 'slug') return applySlug(v, raw);
  return { ...v, [field]: raw } as ConnectAppValues;
}

/** Local validation of every field; an EAS project id only counts when the app builds on EAS. */
export function validateConnectApp(v: ConnectAppValues, onBitrise: boolean): Record<ConnectField, string | undefined> {
  return {
    slug: slugProblem(v.slug),
    name: nameProblem(v.name),
    framework: v.framework ? undefined : 'Pick the framework',
    workingDirectory: workingDirectoryProblem(v.workingDirectory),
    configPath: configPathProblem(v.configPath),
    easProjectId: onBitrise ? undefined : easProjectIdProblem(v.easProjectId),
  };
}

/** What each field shows: the API's message, else the local one after submit (the slug and EAS id also while typing). */
export function connectErrors(local: Record<ConnectField, string | undefined>, serverFields: Record<string, string>, submitted: boolean, v: ConnectAppValues): Record<ConnectField, string | undefined> {
  const at = (field: ConnectField, live = false) => shownError(field, local, serverFields, submitted, live);
  return {
    slug: at('slug', v.slug.length > 0),
    name: at('name'),
    framework: at('framework'),
    workingDirectory: at('workingDirectory'),
    configPath: at('configPath'),
    easProjectId: at('easProjectId', v.easProjectId.length > 0),
  };
}

/** `POST /apps`: the EAS id only for EAS apps, the Bitrise slug only for a native app that links one by hand. */
export function buildConnectBody(projectId: string, v: ConnectAppValues, onBitrise: boolean, createOnBitrise: boolean): AppCreateInput {
  const eas = v.easProjectId.trim();
  return {
    projectId,
    slug: v.slug,
    name: v.name.trim(),
    framework: v.framework as AppFramework,
    workingDirectory: v.workingDirectory.trim(),
    configPath: v.configPath.trim(),
    ...(eas && !onBitrise ? { easProjectId: eas } : {}),
    ...(v.bitriseAppSlug && onBitrise && !createOnBitrise ? { bitriseAppSlug: v.bitriseAppSlug } : {}),
  };
}

/* ---------- Edit app ---------- */

export interface EditAppValues {
  name: string;
  workingDirectory: string;
  configPath: string;
  easProjectId: string;
  bitriseAppSlug: string;
}

export type EditAppField = 'name' | 'workingDirectory' | 'configPath' | 'easProjectId';

export const editValuesOf = (app: App): EditAppValues => ({
  name: app.name,
  workingDirectory: app.workingDirectory,
  configPath: app.configPath,
  easProjectId: app.easProjectId ?? '',
  bitriseAppSlug: app.bitriseAppSlug ?? '',
});

export const validateEditApp = (v: EditAppValues): Record<EditAppField, string | undefined> => ({
  name: nameProblem(v.name),
  workingDirectory: workingDirectoryProblem(v.workingDirectory),
  configPath: configPathProblem(v.configPath),
  easProjectId: easProjectIdProblem(v.easProjectId),
});

/** The Bitrise link shows for native apps once the API builds on Bitrise, and whenever one is already set. */
export const showsBitriseLink = (app: App, bitriseFeature: boolean) => (bitriseFeature && isNativeFramework(app.framework)) || Boolean(app.bitriseAppSlug);

/** Source control is served and the app is native: "Create on Bitrise" and "Sync Bitrise workflows" live in the sheet. */
export const showsBitriseSetup = (app: App, sourceControl: boolean) => sourceControl && (app.framework === 'android_native' || app.framework === 'ios_native');

/** `{ [key]: next }` when the value changed, nothing otherwise: a PATCH sends only what the person changed. */
const changed = <K extends string, V>(key: K, next: V, prev: V) => (next === prev ? {} : ({ [key]: next } as Record<K, V>));

export interface BitriseLink {
  /** The Bitrise field is shown. */
  show: boolean;
  /** Only a slug the person changed is saved: "Create on Bitrise" may set it meanwhile, and a stale form must not unlink it. */
  touched: boolean;
}

/** `PATCH /apps/{id}`: only the changed fields; an empty EAS id or Bitrise slug unlinks (null). */
export function buildEditAppBody(app: App, v: EditAppValues, bitrise: BitriseLink): AppPatchInput {
  return {
    ...changed('name', v.name.trim(), app.name),
    ...changed('workingDirectory', v.workingDirectory.trim(), app.workingDirectory),
    ...changed('configPath', v.configPath.trim(), app.configPath),
    ...changed('easProjectId', v.easProjectId.trim() || null, app.easProjectId ?? null),
    ...(bitrise.show && bitrise.touched ? changed('bitriseAppSlug', v.bitriseAppSlug || null, app.bitriseAppSlug ?? null) : {}),
  };
}
